// includes/js-src/inventory/thumbnail.ts
//
// Inventory thumbnail renderer — carved out of the template.js god-file (2026-06-01,
// inventory→TS Phase 1). The full-featured client mirror of PHP render_inventory_thumbnail():
// builds ONE inventory-item <div> (drag-drop data-*, size/picture/video, HP overlay, qty badge,
// equipment/enchant icons). Used by encounter-update WS, the equipment-slot grid, and the live caster sheet.
//
// All readers/helpers it leans on (ftLayerValue / iconMap / pictureField0 / SIZE_TYPES /
// hasStatusEffect / …) are already early-loaded module globals (core-helpers.js,
// item-arr-readers.js, targeting-combat bundle, template.js inline catalogs) — declared
// ambient in ./globals.d.ts and resolved at call-time. Nothing here runs at module top level.
//
// Behaviour matches the template.js original; the emitted data-* contract is typed explicitly.
// so the post-literal property assignments (unit_weight / cost_gp / trade attrs / …) still
// compile. Never-read data-* copies (v_* vitality details, immovable, difficult_terrain)
// dropped 2026-06-12 — see plans/xz-token-data-contract-and-widget-slimdown.md.

// -------------------------------------------------------------------------------------------
// Item-template default video for map tokens (e.g. looping torch flame).
// Prefers video_primary; otherwise first row with a usable URL.
// -------------------------------------------------------------------------------------------
import { inventoryUnitGp } from '../janus-engine/inventory/unit-gp';
import { queueInventoryActivities } from './don-doff-overlay';
type InventoryRecord = Record<string, unknown>;
type InventoryGroups = Record<string, unknown[]>;

interface InventoryThumbnailOptions {
    show_overlay?: boolean;
    use_size_based_width?: boolean;
    thumbnail_width?: number;
    thumbnail_height?: number | null;
    is_draggable?: boolean;
    show_log_message?: number;
    show_icons?: boolean;
    additional_classes?: string[];
    dom_id?: string | false | null;
    inline_layout?: boolean;
    flow_layout?: boolean;
    exclude_dropzone?: boolean;
    always_show_quantity_badge?: boolean;
    override_thumb_url?: string | null;
    omit_tooltip?: boolean;
    encounter_arr_root?: unknown;
    show_status_effects?: boolean;
    show_smoke?: boolean;
    show_visibility_classes?: boolean;
    trade_owner_ec_id?: string | null;
}

function inventoryRecord(value: unknown): InventoryRecord | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as InventoryRecord
        : null;
}

/**
 * Partner inventory UUIDs linked by integrated_object or locked (caster↔target), excluding self.
 * Matches encounter_arr: lock has status_effect_caster Locked → status_target_id = chest, etc.
 */
function inventoryMapLockTandemPartnerInventoryIds(i: unknown): Set<string> {
    const integratedUuid = VISUAL_STATUS_EFFECTS.integrated_object;
    const lockedUuid = VISUAL_STATUS_EFFECTS.locked;
    const selfId = ftInventoryField(i, 'inventory_id') ?? '';
    const out = new Set<string>();
    const pushUuid = (v: unknown) => {
        if (typeof v === 'string' && checkIfValidUUID(v) && v !== selfId) out.add(v);
    };
    const scan = (list: unknown) => {
        if (!Array.isArray(list)) return;
        for (const eff of list) {
            if (!eff || statusEffectInstanceIsCampaignPaused(eff)) continue;
            const effId = ftItemsCol(eff, 'item_id') ?? '';
            if (effId !== integratedUuid && effId !== lockedUuid) continue;
            // status_target_id/status_caster_id are BARE strings post-descalar (2026-05-27);
            // the old `?.[0]` indexed the first CHARACTER and silently emptied this set.
            // Unwrap-tolerant for legacy [uuid]-wrapped cached rows.
            const unwrapId = (x: unknown) => (Array.isArray(x) ? x[0] : x) ?? '';
            const effect = inventoryRecord(eff);
            pushUuid(unwrapId(effect?.status_target_id));
            pushUuid(unwrapId(effect?.status_caster_id));
        }
    };
    scan(ftCreatureStatuses(i, 'caster'));
    scan(ftCreatureStatuses(i, 'target'));
    return out;
}

function inventoryHasMapLockTandemLink(i: unknown) {
    return inventoryMapLockTandemPartnerInventoryIds(i).size > 0;
}

function inventoryHasCasterStatusEffectUuids(i: unknown, uuids: unknown) {
    const want: unknown[] = Array.isArray(uuids) ? uuids : [uuids];
    const rawList: unknown = ftCreatureStatuses(i, 'caster');
    const list = Array.isArray(rawList) ? rawList : [];
    return list.some((eff) => {
        if (!eff || statusEffectInstanceIsCampaignPaused(eff)) return false;
        const effId = ftItemsCol(eff, 'item_id') ?? '';
        return want.includes(effId);
    });
}

/**
 * Client-side mirror of PHP isInventoryArrAContainer()
 *
 * @param inventory_arr - The inventory object from parsedData.inventory_arr
 * @returns true if the item is a container
 */
function isInventoryArrAContainer(inventory_arr: unknown) {
    // 1. item_type === 'container'
    if (ftItemsCol(inventory_arr, 'item_type') === 'container') {
        return true;
    }

    // 2. Any of the carrying capacity fields exist (even if value is 0 or null).
    // Bucket-aware (capacity layers live in item_layer_types post-reshape; ftLayerValue also
    // resolves ROUND-4 thinned instances via the catalog template def) — the bare top-level
    // probes stay for legacy browser-cached shapes. Mirrors the PHP layer_has() fix.
    if (
        inventoryRecord(inventory_arr)?.carrying_capacity_cu_ft !== undefined ||
        inventoryRecord(inventory_arr)?.carrying_capacity_cu_in !== undefined ||
        inventoryRecord(inventory_arr)?.carrying_capacity_people !== undefined ||
        inventoryRecord(inventory_arr)?.carrying_capacity_pounds !== undefined ||
        inventoryRecord(inventory_arr)?.carrying_capacity_fixed_weight !== undefined ||
        ftLayerValue(inventory_arr, 'carrying_capacity_cu_ft') !== undefined ||
        ftLayerValue(inventory_arr, 'carrying_capacity_cu_in') !== undefined ||
        ftLayerValue(inventory_arr, 'carrying_capacity_people') !== undefined ||
        ftLayerValue(inventory_arr, 'carrying_capacity_pounds') !== undefined ||
        ftLayerValue(inventory_arr, 'carrying_capacity_fixed_weight') !== undefined
    ) {
        return true;
    }

    return false;
}

/**
 * selectInventoryPresentationPicture – primary inventory picture resolver
 * Priority order:
 * 1. inventory_picture (user-uploaded instance art — must win over template art)
 * 2. inventory_presentation_picture / inventory_thumbnail_picture (explicit pointer hydration)
 * 3. inventory_presentation_pic_id lookup inside item_picture
 * 4. Default item_picture (primary or first)
 */
function selectInventoryPresentationPicture(inventory: unknown): unknown {
    // 1. Custom pictures uploaded to this inventory instance
    const _instPics = ftPicturesOf(inventory, 'inventory_picture');
    if (_instPics[0]) {
        return _instPics[0];
    }

    // 2. Dedicated presentation / thumbnail picture rows — via ftPicturesOf, NOT the raw top-level
	//    buckets: v30 retired the duplicated presentation/thumbnail stamp buckets, and the reader's bridge
    //    re-derives the rows from the pointer ids (instance uploads → template-def gallery →
    //    sibling stamp → the v30 catalog PICTURE entry for cross-item art, e.g. a Longsword
    //    whose art rides the "Longswords" base item). The raw reads only ever matched the
    //    pre-v30 stamped/hydrated shapes, so cross-item-art floor tokens rendered blank.
    const presentationRows = ftPicturesOf(inventory, 'inventory_presentation_picture');
    if (presentationRows[0]) return presentationRows[0];
    const _thumbRows = ftPicturesOf(inventory, 'inventory_thumbnail_picture');
    if (_thumbRows[0]) return _thumbRows[0];

    // 3. Presentation ID fallback – look inside the resolved item-picture list
    //    (ftItemPictures → pictures.item bucket; the raw top-level inventory.item_picture
    //    no longer exists after [[project_item_arr_bucket_reshape]], so deref'ing it threw).
    const presentationPicId = ftInventoryField(inventory, 'inventory_presentation_pic_id') || ftInventoryField(inventory, 'inventory_thumbnail_pic_id');
    if (presentationPicId) {
        const presentationPics = ftItemPictures(inventory);
        const match = presentationPics.find((p) => pictureField0(p, 'picture_id') === presentationPicId);
        if (match) return match;
    }

    // 4. Normal base item picture — ftItemPictures() walks carriers via
    //    ftPicturesOf's default-provider descent ([[project_classification_nest_and_walk]]
    //    P1/P2 2026-05-28), so a Longsword without own pics naturally
    //    inherits the Longswords-classification thumb through the nested
    //    carrier inside item_layer_types.weapon_classification.
    if (ftItemPictures(inventory).length > 0) {
        const _invPics = ftItemPictures(inventory); return _invPics.find((p) => pictureField0(p, 'primary_picture') == 1) || _invPics[0];
    }

    return null;
}

// -------------------------------------------------------------------------------------------
// renderInventoryThumbnail – full-featured version (matches PHP render_inventory_thumbnail())
//
// @param inventory            parsedData.inventory_arr
// @param [inventoryGroups]    grouping object (X/Z/size) – used for stacking
// @param [options]            (optional) – all the PHP flags
//   show_overlay            : true   // HP bar, status effects, smoke
//   use_size_based_width    : true   // use SIZE_TYPES thumbnail width
//   thumbnail_width         : 68     // explicit width when use_size_based_width===false
//   thumbnail_height        : null   // explicit height (null = auto)
//   is_draggable            : true   // make the whole <div> draggable
//   show_log_message        : 1      // 0/1 – mirrors old render_inventory_thumbnail()
//   show_icons              : true   // equipment-slot / enchanted icons
//   additional_classes      : []     // extra CSS classes
//   dom_id                  : null   // null → inventory_<uuid>; false → no id; string → custom id
//   inline_layout           : false  // true → relative + block (e.g. trade offer chips)
//   exclude_dropzone        : false  // true → omit dropzone class (nested in another dropzone)
//   always_show_quantity_badge : false // true → quantity-badge for qty ≥ 1 (trade staging)
//   override_thumb_url      : null   // non-null → use this image URL, skip picture + item video
//   omit_tooltip            : false  // true → no bootstrap tooltip attrs
//
// @returns HTML <div id="inventory_…">…</div>
// -------------------------------------------------------------------------------------------
export function renderInventoryThumbnail(inventory: unknown, inventoryGroups: InventoryGroups = {}, options: InventoryThumbnailOptions = {}) {
    queueInventoryActivities();
    const i = inventory;
    const id = ftInventoryField(i, 'inventory_id') ?? '';

    // -----------------------------------------------------------------
    // 0. Normalise options
    // -----------------------------------------------------------------
    const {
        show_overlay            = true,
        use_size_based_width    = true,
        thumbnail_width         = 68,
        thumbnail_height        = null,
        is_draggable            = true,
        show_log_message        = 1,
        show_icons              = true,
        additional_classes      = [],
        dom_id                  = null,
        inline_layout           = false,
        // flow_layout: off-map tiles flow in their container (position:relative, NO auto-margins) —
        // the faithful equivalent of PHP render_inventory_thumbnail's outer div (no position) inside a
        // flex/grid. Distinct from inline_layout (centered chips, margin:auto). Default off keeps the XZ
        // map's position:absolute. ft_inventory_tile_mount() turns this on for off-map callers.
        flow_layout             = false,
        exclude_dropzone        = false,
        always_show_quantity_badge = false,
        override_thumb_url      = null,
        omit_tooltip            = false,
        encounter_arr_root      = null,
        // Parity gates with PHP render_inventory_thumbnail(), default OFF so the XZ map
        // (which calls this renderer directly and composites status/hidden via
        // animateStatusEffectChanges → .status-effect-overlay) never double-renders them.
        // ft_inventory_tile_mount() turns these ON for off-map callers (derived from the old
        // $show_overlay, which is what gated all three in PHP).
        show_status_effects     = false,   // PHP overlay: up to 4 target status icons + overflow
        show_smoke              = false,   // PHP smoke-overlay when hidden
        show_visibility_classes = false,   // PHP object-hidden / object-invisible / object-ethereal
        trade_owner_ec_id       = null,    // PHP data-trade_owner_ec_id (commerce UI)
    } = options;

    // -----------------------------------------------------------------
    // 1. Size → width / base size
    // -----------------------------------------------------------------
    // Phase 3 Target B (2026-05-27): i.size is now a scalar UUID for single-valued
    // layer emission; legacy cached blobs still arrive [uuid]-wrapped during Redis rollover.
    // ⛔ EFFECTIVE size, not the template's. `ftLayerValue(i,'size')` returns the ITEM's size and
    //    drew every collapsed door/window at its BASE size (a Large stone door as a Medium standee)
    //    after the 2026-09-04 size-variant collapse moved the real size into inventory_size.
    //    NULL = inherit, so un-overridden rows are byte-identical to before.
    const _iSizeId = ftEffectiveSizeId(i);
    const sizeInfo = SIZE_TYPES.find((s) => s.item_id === (_iSizeId ?? SIZE_TYPES[0].item_id)) || SIZE_TYPES[0];
    const baseFt   = sizeInfo.base_size;
    const sizeName = sizeInfo.variable_name;
    const thumbW = use_size_based_width ? sizeInfo.thumbnail_width : thumbnail_width;

    // -----------------------------------------------------------------
    // 2. Picture. FT3D owns prop animation; uploaded videos remain gallery media.
    // -----------------------------------------------------------------
    const pic = override_thumb_url ? null : selectInventoryPresentationPicture(inventory);

    // -----------------------------------------------------------------
    // 3. Height calculation
    // -----------------------------------------------------------------
    let pixelH = 0, heightFt = baseFt;
    if (thumbnail_height !== null) {
        pixelH   = thumbnail_height;
        heightFt = +(pixelH / thumbW * baseFt).toFixed(2);
    } else if (override_thumb_url) {
        pixelH = thumbW;
        heightFt = +(pixelH / thumbW * baseFt).toFixed(2);
    } else {
        const pw = pictureField0(pic, 'picture_width');
        const ph = pictureField0(pic, 'picture_height');
        if (pw && ph) {
            const ratio = ph / pw;
            pixelH   = Math.round(ratio * thumbW);
            heightFt = +(pixelH / thumbW * baseFt).toFixed(2);
        }
    }
    if (!pixelH) pixelH = thumbW;

    // -----------------------------------------------------------------
    // 4. Position & stacking
    // -----------------------------------------------------------------
    const xPos = ftPosCoord(i, 'x_pos') ?? 0;
    const zPos = ftPosCoord(i, 'z_pos') ?? 0;
    const groupKey = `${xPos}_${zPos}_${sizeName}`;
    const isFirstInGroup = inventoryGroups[groupKey] && ftInventoryField(inventoryGroups[groupKey][0], 'inventory_id') === id;

    // -----------------------------------------------------------------
    // 5. State flags
    // -----------------------------------------------------------------
    const isContainer   = isInventoryArrAContainer(i);
    const dropzoneType  = isContainer ? 'dropzone_container' : 'dropzone_stackable_inventory';
    const modifyEncounterTools = ftIsModifyEncounterToolsPage();
    const hasCampaignGmDragPerm = ftHasCampaignPermission(i);
    // Accept EITHER slot, picking whichever actually YIELDS a value once unwrapped.
    // ft_stamp_viewer_inventory_map_edit() writes both, but the two can drift apart in flight:
    // encounter-delta.ts restores the top-level and item_layer_types copies independently, and
    // its top-level restore is skipped whenever the patched blob left a non-null value there —
    // so an empty-array top level can coexist with a valid item_layer_types 1. A plain `??`
    // would stop at that empty array and read undefined, silently pinning the GM's props again.
    const encounterRoot = inventoryRecord(encounter_arr_root);
    const unwrapMapEdit = (raw: unknown): unknown => (Array.isArray(raw) ? raw[0] : raw);
    const viewerMapEdit = [
        encounterRoot?.viewer_inventory_map_edit,
        inventoryRecord(encounterRoot?.item_layer_types)?.viewer_inventory_map_edit,
    ].map(unwrapMapEdit).find((value) => value !== undefined && value !== null);
    const viewerMapEditGm = viewerMapEdit == 1 || viewerMapEdit === '1';
    // Scene-fixed props (immovable layer / integrated_object status) are a PLAYER-facing drag
    // block only: the GM, the map editor and the modify-encounter tools surface may always
    // reposition them. move_draggable_inventory_inc.php remains the authoritative gate — this
    // just stops the client refusing before the request is ever made.
    const viewerCanDragFixedProps = modifyEncounterTools || hasCampaignGmDragPerm || viewerMapEditGm;
    const isImmovable   = !viewerCanDragFixedProps && !!ftLayerValue(i, 'immovable');
    const tandemLockLink = inventoryHasMapLockTandemLink(i);
    // Tandem lock/integrated pairs can both be dragged; integrated_object on caster side lives in status_effect_caster.
    // The viewerCanDragFixedProps escape hatch mirrors isImmovable — its absence here is what
    // pinned every already-placed digsite (and every monster_bit / fabricated world object) in
    // place for the GM, since those rows carry integrated_object rather than the immovable layer.
    const isIntegrated  = !tandemLockLink && !viewerCanDragFixedProps && (!!ftLayerValue(i, 'integrated_object')
        || hasStatusEffect(i, VISUAL_STATUS_EFFECTS.integrated_object)
        || inventoryHasCasterStatusEffectUuids(i, VISUAL_STATUS_EFFECTS.integrated_object));
    const isLocked      = hasStatusEffect(i, VISUAL_STATUS_EFFECTS.locked);
    // Locked doors/containers can still be dragged on the XZ map; lock pieces with caster↔target link move in tandem server-side.
    const itemTypeForDrag = ftItemsCol(i, 'item_type') ?? '';
    const isLockAnchorOnMap = isLocked && (isContainer || itemTypeForDrag === 'door');
    const draggable = modifyEncounterTools
        ? !!is_draggable
        : is_draggable && !isImmovable && !isIntegrated && (!isLocked || isLockAnchorOnMap || tandemLockLink);

    // Visibility state (mirrors PHP check_target_hidden / _invisible / _ethereal — each just a
    // has_status_effect check against the VISUAL_STATUS_EFFECTS UUIDs). Only painted into classes /
    // smoke when the off-map parity gates are set; the XZ map leaves them off.
    const isWares = Number(ftInventoryField(i, 'inventory_wares') ?? 0) === 1;

    const isHidden    = hasStatusEffect(i, VISUAL_STATUS_EFFECTS.hidden);
    const isInvisible = hasStatusEffect(i, VISUAL_STATUS_EFFECTS.invisible)
        || hasStatusEffect(i, VISUAL_STATUS_EFFECTS.greater_invisible);
    const isEthereal  = hasStatusEffect(i, VISUAL_STATUS_EFFECTS.ethereal);

    // -----------------------------------------------------------------
    // 6. CSS classes – NO visibility classes here anymore
    // -----------------------------------------------------------------
    const classes = [
        'inventory-item',
        exclude_dropzone ? '' : 'dropzone',
        draggable ? 'draggable draggable-line' : 'cursor-pointer',
        ftLayerValue(i, 'object_does_not_grant_environmental_cover') ? 'no-environmental-cover' : '',
        sizeName,
        Object.keys(inventoryGroups).length ? 'inventory-item-absolute' : '',
        // PHP gated these under $show_overlay; here they have a dedicated gate so the XZ map (which
        // reveals hidden via animateStatusEffectChanges) is unaffected.
        show_visibility_classes && isHidden    ? 'object-hidden'    : '',
        show_visibility_classes && isInvisible ? 'object-invisible' : '',
        show_visibility_classes && isEthereal  ? 'object-ethereal'  : '',
        isWares ? 'ft-inv-wares' : '',
        ...additional_classes
    ].filter(Boolean);

    const displayInventoryName = decodeHtmlEntitiesForPlainText(ftInventoryField(i, 'inventory_name') ?? '');
    const name = displayInventoryName !== '' ? displayInventoryName : '???';

    // -----------------------------------------------------------------
    // 7. data-* attributes
    // -----------------------------------------------------------------
    const data: {
        draggable_id: unknown;
        inventory_id: unknown;
        move_from_inventory_id: unknown;
        possess_id: unknown;
        inventory_qty: unknown;
        inventory_name: string;
        inventory_item_id: unknown;
        item_type: unknown;
        enchantment_template_id: unknown;
        x: unknown;
        z: unknown;
        size: string;
        show_log_message: unknown;
        thumbnail_width: number;
        thumbnail_height: number;
        dropzone_id: unknown;
        dropzone_type: string;
        bright_light_dim_light: unknown;
        magical_light: unknown;
        magical_darkness: unknown;
        width_ft: number;
        height_ft: number;
        v_bar_v_percent: unknown;
        v_bar_v_temp_percent: unknown;
        unit_weight: unknown;
        cost_gp: number;
        item_id: string;
        has_donning_doffing: number;
        is_wares: number;
        trade_owner_ec_id?: string;
    } = {
        draggable_id: id,
        inventory_id: id,
        move_from_inventory_id: ftInventoryField(i, 'possess_id') ?? '',
        possess_id: ftInventoryField(i, 'possess_id') ?? '',
        inventory_qty: ftInventoryField(i, 'item_qty') ?? 1,
        inventory_name: displayInventoryName,
        inventory_item_id: ftInventoryField(i, 'inventory_item_id') ?? '',
        item_type: ftItemsCol(i, 'item_type') ?? '',
        enchantment_template_id: ftLayerValue(i, 'enchantment_template_id') ?? '',
        x: xPos,
        z: zPos,
        size: sizeName,
        show_log_message,
        thumbnail_width: thumbW,
        thumbnail_height: pixelH,
        dropzone_id: id,
        dropzone_type: dropzoneType,
        bright_light_dim_light: ftLayerValue(i, 'bright_light_dim_light') ?? 0,
        magical_light: ftLayerValue(i, 'magical_light') ?? 0,
        magical_darkness: ftLayerValue(i, 'magical_darkness') ?? 0,
        width_ft: baseFt,
        height_ft: heightFt,
        // Prev-frame animation slots — animate-entity.ts reads these off the OLD element as
        // the previous render's value to tween vitality bars from. Genuinely DOM state; the
        // other vitality detail fields live in computed.vitality and are read from the blob
        // (stale data-* copies removed 2026-06-12).
        v_bar_v_percent:    ftComputedValue(i, 'vitality', 'v_bar_v_percent') ?? 0,
        v_bar_v_temp_percent: ftComputedValue(i, 'vitality', 'v_bar_v_temp_percent') ?? 0,
        unit_weight: ftLayerValue(i, 'weight') ?? 0,
        cost_gp: 0,
        item_id: String(ftItemsCol(i, 'item_id') ?? ''),
        has_donning_doffing: 0,
        is_wares: isWares ? 1 : 0,
    };

    data.cost_gp = inventoryUnitGp(i as InventoryRecord, {
        inventoryField: ftInventoryField, value: ftLayerValue,
        itemId: row => String(ftItemsCol(row, 'item_id') ?? ''), computed: ftComputedValue,
    });
    const _donning   = parseFloat(String(ftLayerValue(i, 'donning_time_int') ?? 0)) || 0;
    const _doffing   = parseFloat(String(ftLayerValue(i, 'doffing_time_int') ?? 0)) || 0;
    data.has_donning_doffing = (_donning > 0 || _doffing > 0) ? 1 : 0;
    if (trade_owner_ec_id && checkIfValidUUID(trade_owner_ec_id)) {
        data.trade_owner_ec_id = String(trade_owner_ec_id);
    }

    // Token data contract (2026-06-12): gross_weight + carrying_capacity_remaining_pounds
    // attribute copies retired — DragManager resolves them from encounterArr via
    // encounter-map/token-data.ts (dataset fallback still honors PHP-rendered sheet elements).
    // NOTE: isImmovable still gates `draggable` above and the badge overlay renders from the
    // layer value — only the unread data-immovable / data-difficult_terrain attribute copies
    // were dropped (no reader anywhere, audit 2026-06-12).

    const dataStr = Object.entries(data)
        .filter(([, v]) => v != null && v !== '')
        .map(([k, v]) => `data-${k}="${escapeHtml(String(v))}"`)
        .join(' ');

	// -----------------------------------------------------------------
	// 8. Thumbnail image / initials
	// -----------------------------------------------------------------
	let thumbInner = '';
	if (override_thumb_url) {
		const safeU = escapeHtml(String(override_thumb_url));
		thumbInner = `<img src="${safeU}" class="d-block token-img"
						  style="pointer-events:none;" alt="${escapeHtml(name)}">`;
	} else {
	const picUrl = pictureField0(pic, 'picture_url');

	if (picUrl) {
		thumbInner = `<img src="${picUrl}" class="d-block token-img"
						  style="pointer-events:none;" alt="${escapeHtml(name)}">`;
	} else {
		thumbInner = `<div class="bg-warning border border-dark rounded d-flex justify-content-center align-items-center"
						  style="width:100%;height:100%;">
						  <span class="fw-bold h4 text-dark">${escapeHtml(name.slice(0, 2).toUpperCase())}</span>
					  </div>`;
	}
	}

    // -----------------------------------------------------------------
    // 9. Icons (enchant / equipment slot)
    // -----------------------------------------------------------------
    let iconHtml = '';
    if (show_icons) {
        if (ftLayerValue(i, 'enchantment_template_id')) iconHtml += iconMap('item_enchanted', 15, 15);
        // equipment_slot is a layer now ([[project_item_arr_bucket_reshape]]); ftLayerValue returns the
        // resolved scalar. The old raw i.equipment_slot[0] is undefined → threw, so use the resolved value.
        const _equipSlot = ftLayerValue(i, 'equipment_slot');
        if (_equipSlot)                                 iconHtml += iconMap(_equipSlot, 15, 15);
        if (iconHtml) iconHtml = `<span class="position-absolute" style="top:-5px;right:0;z-index:3;pointer-events:none;">${iconHtml}</span>`;
    }
    // Wares (mark-to-sell) coin badge — bottom-LEFT (top-right = enchant/slot icons, bottom-right =
    // qty badge). Deliberately outside the show_icons gate: the sheet's on-hand grid passes
    // show_icons:false yet must still show the flag. Non-interactive; toggling happens via
    // shift-click / info panel / edit form.
    if (isWares) {
        iconHtml += `<span class="ft-inv-wares-badge position-absolute" style="bottom:-2px;left:0;z-index:3;pointer-events:none;">${iconMap('merchant', 15, 15)}</span>`;
    }

	// -----------------------------------------------------------------
	// 9.5 QUANTITY BADGE – Bottom-right, direct positioning (no extra wrapper)
	// -----------------------------------------------------------------
	let qtyBadge = '';
	const qty = parseInt(ftInventoryField(i, 'item_qty') ?? 1, 10);
	const showQtyBadge = always_show_quantity_badge ? qty >= 1 : qty > 1;
	if (showQtyBadge) {
		const displayQty = qty >= 1000 ? `${(qty / 1000).toFixed(1)}k` : qty;
		qtyBadge = `
			<div class="position-absolute bottom-0 end-0 inventory-qty-container" style="padding:4px 6px; pointer-events:none; z-index:4;">
				<div class="quantity-badge">${displayQty}</div>
			</div>`;
	}

    // -----------------------------------------------------------------
    // 10. HP bar
    // -----------------------------------------------------------------
    let hpBlock = '';
    if (show_overlay) {
        const vCurrent = ftComputedValue(i, 'vitality', 'v_current') ?? 0;
        const vMax     = ftComputedValue(i, 'vitality', 'v_max') ?? 0;
        const hpPct    = ftComputedValue(i, 'vitality', 'v_bar_v_percent') ?? 0;
        const tempPct  = ftComputedValue(i, 'vitality', 'v_bar_v_temp_percent') ?? 0;

        let hpFill = `<div class="progress-bar bg-danger" style="width:${hpPct}%"></div>`;
        if (tempPct > 0) {
            hpFill += `<div class="progress-bar" style="width:${tempPct}%;background:linear-gradient(to right,#ffc107,#ffeb3b);"></div>`;
        }

        const hpLabel = `${vCurrent}/${vMax}`;
        hpBlock = `
            <div class="hp-bar" data-hp-label="${hpLabel}">
                <div class="progress border border-dark">${hpFill}</div>
            </div>`;
    }

    // -----------------------------------------------------------------
    // 11. Status-effects overlay (off-map parity; PHP render_inventory_thumbnail lines 305-327)
    // up to 4 target status icons + a "show more" overflow. Spans get pointer-events:auto so they
    // stay clickable under the pointer-events-none overlay (the map keeps that for drag pass-through).
    // -----------------------------------------------------------------
    let statusEffectsHtml = '';
    if (show_status_effects) {
        const effs = ftCreatureStatuses(i, 'target') || [];
        if (effs.length) {
            statusEffectsHtml += '<div class="status-effects d-flex flex-wrap gap-1">';
            let cnt = 0;
            for (const eff of effs) {
                if (++cnt > 4) break;
                const effHref = String(ftItemsCol(eff, 'item_id') ?? '');
                const typeStr = String(ftItemsCol(eff, 'item_type') ?? '');
                const iconId  = typeStr === 'status_effect' ? effHref : typeStr;
                statusEffectsHtml += `<span role="button" class="d-block" onclick="openItemInfo('${escapeHtml(effHref)}')" style="line-height:1;pointer-events:auto;">${iconMap(iconId, 14, 14)}</span>`;
            }
            if (effs.length > 4) {
                statusEffectsHtml += `<span role="button" class="d-block" onclick="showStatusEffects('${escapeHtml(id)}')" style="line-height:1;pointer-events:auto;">${iconMap('show_status_effects', 14, 14)}</span>`;
            }
            statusEffectsHtml += '</div>';
        }
    }

    // Smoke overlay when hidden (PHP lines 331-336) — sibling of the overlay, like PHP.
    const smokeHtml = (show_smoke && isHidden)
        ? `<div class="smoke-overlay">${'<span></span>'.repeat(8)}</div>`
        : '';

    // -----------------------------------------------------------------
    // 12. Static overlay (HP, status effects, icons)
    // -----------------------------------------------------------------
    const overlayInner = `${hpBlock}${statusEffectsHtml}`;
    const staticOverlay = (show_overlay || statusEffectsHtml)
        ? `<div class="inventory-item-overlay position-absolute top-0 start-0 w-100 h-100 pointer-events-none" style="z-index:3;">
            ${overlayInner}
            ${iconHtml}
        </div>`
        : iconHtml
          ? `<div class="position-absolute top-0 start-0 w-100 h-100 pointer-events-none" style="z-index:3;">${iconHtml}</div>`
          : '';

    // -----------------------------------------------------------------
    // 13. Content wrapper
    // -----------------------------------------------------------------
    const contentHtml = `
        <div class="position-relative d-block w-100 h-100" style="z-index:0;--tw:${thumbW}px;--th:${pixelH}px;">
            <div class="d-flex align-items-center justify-content-center w-100 h-100">
                ${thumbInner}
            </div>
            ${staticOverlay}
            ${smokeHtml}
            ${qtyBadge}
        </div>`;

    // -----------------------------------------------------------------
    // 14. Inline style
    // -----------------------------------------------------------------
    let inlineStyle = inline_layout
        ? `opacity:1;width:${thumbW}px;height:${pixelH}px;position:relative;margin-left:auto;margin-right:auto;`
        : flow_layout
          ? `opacity:1;width:${thumbW}px;height:${pixelH}px;position:relative;`
          : `opacity:1;width:${thumbW}px;height:${pixelH}px;position:absolute;`;
    if (Object.keys(inventoryGroups).length) {
        inlineStyle += `display:${isFirstInGroup ? 'block' : 'none'};`;
    } else {
        inlineStyle += `display:block;`;
    }

    // -----------------------------------------------------------------
    // 15. Final HTML
    // -----------------------------------------------------------------
    let idAttr = ` id="inventory_${escapeHtml(id)}"`;
    if (dom_id === false) {
        idAttr = '';
    } else if (typeof dom_id === 'string' && dom_id !== '') {
        idAttr = ` id="${escapeHtml(dom_id)}"`;
    }
    const tooltipAttrs = omit_tooltip
        ? ''
        : ` data-bs-toggle="tooltip"
             data-bs-placement="top"
             title="${escapeHtml(displayInventoryName)}"
             data-bs-title="${escapeHtml(displayInventoryName)}"`;

    return `
        <div${idAttr}
             class="${classes.join(' ')}"${tooltipAttrs}
             ${dataStr}
             style="${inlineStyle}">
            ${contentHtml}
        </div>`;
}

// -------------------------------------------------------------------------------------------
// inventoryMapTokenMediaSignature — the inventory analogue of creatureMapTokenMediaSignature.
// A short string identifying the art an XZ inventory token SHOULD show, so a media change
// (carousel xz/thumbnail pic swap, instance picture upload) is detectable.
// -------------------------------------------------------------------------------------------
export function inventoryMapTokenMediaSignature(inventory: unknown): string {
    const pic = selectInventoryPresentationPicture(inventory);
    if (pic) {
        const picUrl = pictureField0(pic, 'picture_url');
        if (picUrl) return `image:${picUrl}`;
    }
    return `init:${ftInventoryField(inventory, 'inventory_id') ?? ''}`;
}

// -------------------------------------------------------------------------------------------
// syncInventoryTokenThumbMediaIfChanged — inventory analogue of syncCreatureTokenThumbMediaIfChanged.
// Hot-swaps ONLY the inner <img>/<video>/initials node of a live XZ inventory token when the blob's
// art no longer matches what's painted, preserving the outer element (id, position, drag binding) and
// its overlay/badges. The "currently painted" signature is read from the DOM media node itself rather
// than a pre-stamped data-* — so a freshly-changed picture repaints on the FIRST encounter_update (no
// first-sync skip to satisfy), and SSR/client-built tokens behave identically. This is what lets a
// floor item's picture update by "reading truth" (the encounterArr blob) instead of a full widget rebuild.
// -------------------------------------------------------------------------------------------
export function syncInventoryTokenThumbMediaIfChanged(mapTokenEl: HTMLElement, inventory: unknown): void {
    // Inventory token DOM (renderInventoryThumbnail §13): outer div >
    //   .position-relative.d-block.w-100.h-100 > .d-flex…justify-content-center > <img|video|initials>
    const wrap = mapTokenEl.querySelector<HTMLElement>('.position-relative.d-block.w-100.h-100');
    const mediaHolder = wrap
        ? wrap.querySelector<HTMLElement>(':scope > .d-flex.align-items-center.justify-content-center')
        : null;
    if (!mediaHolder || !mediaHolder.firstElementChild) {
        return;
    }

    const nextSig = inventoryMapTokenMediaSignature(inventory);

    // Derive what's CURRENTLY shown straight from the painted media node, so we never depend on a
    // stamped signature surviving SSR / partial re-renders.
    let prevSig = 'init:';
    const curMedia = mediaHolder.querySelector('video.token-img, img.token-img');
    if (curMedia) {
        if (curMedia.tagName === 'VIDEO') {
            const s = curMedia.querySelector('source')?.getAttribute('src') || '';
            prevSig = `video:${s}`;
        } else {
            prevSig = `image:${curMedia.getAttribute('src') || ''}`;
        }
    }

    // No resolvable new media (initials fallback) or already correct → nothing to do.
    if (nextSig.startsWith('init:') || nextSig === prevSig) {
        return;
    }

    const temp = document.createElement('div');
    temp.innerHTML = renderInventoryThumbnail(inventory, {}, { show_overlay: false }).trim();
    const freshOuter = temp.firstElementChild;
    const newHolder = freshOuter
        ? freshOuter.querySelector('.position-relative.d-block.w-100.h-100 > .d-flex.align-items-center.justify-content-center')
        : null;
    const newMedia = newHolder ? newHolder.firstElementChild : null;
    if (!newMedia) {
        return;
    }
    mediaHolder.replaceChild(newMedia.cloneNode(true), mediaHolder.firstElementChild);
}
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
